import { useCallback, useEffect, useRef, useState } from 'react';
import { isFunction } from 'lodash';

import { difference, equals } from '../../../../../common/maths/geometry/point';
import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';
import {
    clampZoomScale,
    clampZoomTranslation,
    DEFAULT_IMAGE_MODAL_MAX_SCALE,
    DEFAULT_IMAGE_MODAL_MIN_SCALE,
    DEFAULT_IMAGE_MODAL_ZOOM_STEP,
    getNextPannedZoomState,
    getNextZoomState,
    getNextZoomStateFromWheelDelta,
    ImageModalZoomDimensions,
    ImageModalZoomState,
    resetZoomState,
} from '../utils/imageModalZoomUtils';

export type UseImageModalZoomResult = ImageModalZoomState &
    ImageModalZoomDimensions & {
        isPanning: boolean;
        isZoomed: boolean;
        endPan: () => void;
        panBy: (panDelta: Point) => void;
        resetZoom: () => void;
        setContentSize: (contentSize: RectSize) => void;
        setTranslation: (translation: Point) => void;
        setViewportSize: (viewportSize: RectSize) => void;
        startPan: (point: Point) => boolean;
        updatePan: (point: Point) => void;
        zoomIn: (anchorPoint?: Point) => void;
        zoomOut: (anchorPoint?: Point) => void;
        zoomToPoint: (anchorPoint: Point, scaleDelta: number) => void;
        zoomWheelToPoint: (anchorPoint: Point, wheelDeltaY: number) => void;
    };

const EMPTY_SIZE: RectSize = {
    width: 0,
    height: 0,
};

const areSizesEqual = (sizeA: RectSize, sizeB: RectSize): boolean =>
    sizeA.width === sizeB.width && sizeA.height === sizeB.height;

const getViewportCenter = (viewportSize: RectSize): Point => ({
    x: viewportSize.width / 2,
    y: viewportSize.height / 2,
});

const useImageModalZoom = ({
    maxScale = DEFAULT_IMAGE_MODAL_MAX_SCALE,
    minScale = DEFAULT_IMAGE_MODAL_MIN_SCALE,
    zoomStep = DEFAULT_IMAGE_MODAL_ZOOM_STEP,
}: {
    maxScale?: number;
    minScale?: number;
    zoomStep?: number;
} = {}): UseImageModalZoomResult => {
    const [zoomState, setZoomState] = useState<ImageModalZoomState>(resetZoomState);
    const [viewportSize, setViewportSizeState] = useState<RectSize>(EMPTY_SIZE);
    const [contentSize, setContentSizeState] = useState<RectSize>(EMPTY_SIZE);
    const [isPanning, setIsPanning] = useState(false);
    const previousPanPointRef = useRef<Point | null>(null);
    // These refs serve two related but distinct purposes:
    // 1. Read the latest rendered values inside stable event callbacks.
    // 2. Allow same-tick imperative overrides in setters before React has re-rendered.
    //    That second case is why we still assign to `.current` manually in the setters below.
    const zoomStateRef = useUpdatedRef(zoomState);
    const viewportSizeRef = useUpdatedRef(viewportSize);
    const contentSizeRef = useUpdatedRef(contentSize);

    const updateZoomState = useCallback(
        (updater: ImageModalZoomState | ((currentZoomState: ImageModalZoomState) => ImageModalZoomState)) => {
            setZoomState((currentZoomState) => {
                const nextZoomState = isFunction(updater) ? updater(currentZoomState) : updater;
                zoomStateRef.current = nextZoomState;
                return nextZoomState;
            });
        },
        [],
    );

    const panBy = useCallback(
        (panDelta: Point) => {
            updateZoomState((currentZoomState) =>
                getNextPannedZoomState({
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    panDelta,
                    zoomState: currentZoomState,
                }),
            );
        },
        [updateZoomState],
    );

    const setViewportSize = useCallback((nextViewportSize: RectSize) => {
        viewportSizeRef.current = nextViewportSize;
        setViewportSizeState((currentViewportSize) =>
            areSizesEqual(currentViewportSize, nextViewportSize) ? currentViewportSize : nextViewportSize,
        );
    }, []);

    const setContentSize = useCallback((nextContentSize: RectSize) => {
        contentSizeRef.current = nextContentSize;
        setContentSizeState((currentContentSize) =>
            areSizesEqual(currentContentSize, nextContentSize) ? currentContentSize : nextContentSize,
        );
    }, []);

    useEffect(() => {
        updateZoomState((currentZoomState) => {
            const clampedScale = clampZoomScale(currentZoomState.scale, { minScale, maxScale });
            const clampedTranslation = clampZoomTranslation(currentZoomState.translation, {
                scale: clampedScale,
                translation: currentZoomState.translation,
                viewportSize,
                contentSize,
            });

            const hasReachedLimits =
                clampedScale === currentZoomState.scale && equals(clampedTranslation, currentZoomState.translation);

            if (hasReachedLimits) return currentZoomState;

            return {
                scale: clampedScale,
                translation: clampedTranslation,
            };
        });
    }, [contentSize, maxScale, minScale, updateZoomState, viewportSize]);

    const resetZoom = useCallback(() => {
        previousPanPointRef.current = null;
        setIsPanning(false);
        updateZoomState(resetZoomState());
    }, [updateZoomState]);

    const setTranslation = useCallback(
        (nextTranslation: Point) => {
            updateZoomState((currentZoomState) => {
                const clampedTranslation = clampZoomTranslation(nextTranslation, {
                    scale: currentZoomState.scale,
                    translation: nextTranslation,
                    viewportSize: viewportSizeRef.current,
                    contentSize: contentSizeRef.current,
                });

                if (equals(clampedTranslation, currentZoomState.translation)) {
                    return currentZoomState;
                }

                return {
                    ...currentZoomState,
                    translation: clampedTranslation,
                };
            });
        },
        [updateZoomState],
    );

    const zoomToPoint = useCallback(
        (anchorPoint: Point, scaleDelta: number) => {
            updateZoomState((currentZoomState) =>
                getNextZoomState({
                    anchorPoint,
                    bounds: { minScale, maxScale },
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    scaleDelta,
                    zoomState: currentZoomState,
                }),
            );
        },
        [maxScale, minScale, updateZoomState],
    );

    const zoomWheelToPoint = useCallback(
        (anchorPoint: Point, wheelDeltaY: number) => {
            updateZoomState((currentZoomState) =>
                getNextZoomStateFromWheelDelta({
                    anchorPoint,
                    bounds: { minScale, maxScale },
                    dimensions: {
                        viewportSize: viewportSizeRef.current,
                        contentSize: contentSizeRef.current,
                    },
                    wheelDeltaY,
                    zoomState: currentZoomState,
                }),
            );
        },
        [maxScale, minScale, updateZoomState],
    );

    const zoomIn = useCallback(
        (anchorPoint = getViewportCenter(viewportSizeRef.current)) => {
            zoomToPoint(anchorPoint, zoomStep);
        },
        [zoomStep, zoomToPoint],
    );

    const zoomOut = useCallback(
        (anchorPoint = getViewportCenter(viewportSizeRef.current)) => {
            zoomToPoint(anchorPoint, -zoomStep);
        },
        [zoomStep, zoomToPoint],
    );

    const startPan = useCallback(
        (point: Point): boolean => {
            if (zoomStateRef.current.scale <= minScale) return false;

            previousPanPointRef.current = point;
            setIsPanning(true);
            return true;
        },
        [minScale],
    );

    const updatePan = useCallback(
        (point: Point) => {
            if (!previousPanPointRef.current) return;

            const panDelta = difference(previousPanPointRef.current, point);

            previousPanPointRef.current = point;
            panBy(panDelta);
        },
        [panBy],
    );

    const endPan = useCallback(() => {
        previousPanPointRef.current = null;
        setIsPanning(false);
    }, []);

    return {
        ...zoomState,
        viewportSize,
        contentSize,
        isPanning,
        isZoomed: zoomState.scale > minScale,
        setViewportSize,
        setContentSize,
        setTranslation,
        panBy,
        zoomToPoint,
        zoomWheelToPoint,
        zoomIn,
        zoomOut,
        resetZoom,
        startPan,
        updatePan,
        endPan,
    };
};

export default useImageModalZoom;
